import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Input,
  makeStyles,
  Option,
  tokens
} from "@fluentui/react-components";
import React from "react";
import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { IAppFeaturesDataGrid } from "./IAppFeaturesDataGrid";

const scopeList = ['personal', 'groupChat', 'team']

const contextList = [
  "personalTab",
  "channelTab",
  "privateChatTab",
  "meetingChatTab",
  "meetingDetailsTab",
  "meetingSidePanel",
  "meetingStage",
  "teamLevelApp"
]

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalL,
  },
  actions: {
    marginTop: tokens.spacingVerticalL,
  },
  error: {
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
    marginTop: tokens.spacingVerticalXXS,
  }
});

export const AppFeaturesDialog: React.FC<
  {
    open: boolean,
    setOpen: (value: boolean) => void,
    activeItem: IAppFeaturesDataGrid,
    onAddOrEdit: (item: IAppFeaturesDataGrid) => void
  }
> = ({ open, setOpen, activeItem, onAddOrEdit }) => {
  const intl = useIntl();
  const classes = useStyles();
  const [item, setItem] = React.useState<IAppFeaturesDataGrid>(activeItem);
  const [hasError, setHasError] = React.useState(true);
  const [hasUrlError, setHasUrlError] = React.useState(false);
  const [hasWebsiteUrlError, setHasWebsiteUrlError] = React.useState(false);

  React.useEffect(() => {
    if (item && !item.objectId) {
      setItem({ ...item, objectId: uuidv4() });
    }
    if (item && !item.entityId) {
      setItem({ ...item, entityId: uuidv4() });
    }
    if (item) {
      setHasError(!item.name ||
        !item.contentUrl ||
        !item.entityId ||
        item?.scopes?.length === 0
      );
    }
  }, [item]);


  React.useEffect(() => {
    if (item.contentUrl && !/^https:\/\//.test(item.contentUrl)) {
      setHasUrlError(true);
    } else {
      setHasUrlError(false);
    }
    if (item.websiteUrl && !/^https:\/\//.test(item.websiteUrl)) {
      setHasWebsiteUrlError(true);
    } else {
      setHasWebsiteUrlError(false);
    }
  }, [item.contentUrl, item.websiteUrl]);

  return (
    <Dialog open={open}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>
            {intl.formatMessage({ defaultMessage: "Add a tab to your personal app" })}
          </DialogTitle>
          <DialogContent className={classes.container}>
            <Field label={intl.formatMessage({ defaultMessage: "Tab name" })} required>
              <Input
                placeholder={intl.formatMessage({ defaultMessage: "Enter a name for your tab" })}
                value={item.name ?? ''}
                onChange={(e) => setItem({ ...item, name: e.target.value })}
              />
            </Field>
            <Field label={intl.formatMessage({ defaultMessage: "Entity ID" })} required>
              <Input
                placeholder={intl.formatMessage({ defaultMessage: "Enter a entity ID" })}
                value={item.entityId ?? ''}
                onChange={(e) => setItem({ ...item, entityId: e.target.value })}
              />
            </Field>
            <Field label={intl.formatMessage({ defaultMessage: "Content URL" })} required>
              <Input
                className={hasUrlError ? classes.error : ''}
                placeholder={intl.formatMessage({ defaultMessage: "Enter the content URL" })}
                value={item.contentUrl ?? ''}
                onChange={(e) => setItem({ ...item, contentUrl: e.target.value })}
              />
              {hasUrlError && (
                <span className={classes.errorText}>
                  {intl.formatMessage({ defaultMessage: "Please enter a valid URL starting with https://" })}
                </span>
              )}
            </Field>
            <Field label={intl.formatMessage({ defaultMessage: "Website URL" })}>
              <Input
                className={hasWebsiteUrlError ? classes.error : ''}
                placeholder={intl.formatMessage({ defaultMessage: "Enter the website URL" })}
                value={item.websiteUrl ?? ''}
                onChange={(e) => setItem({ ...item, websiteUrl: e.target.value })}
              />
              {hasWebsiteUrlError && (
                <span className={classes.errorText}>
                  {intl.formatMessage({ defaultMessage: "Please enter a valid URL starting with https://" })}
                </span>
              )}
            </Field>
            <Field label={intl.formatMessage({ defaultMessage: "Scope" })} required>
              <Dropdown
                multiselect
                placeholder={intl.formatMessage({ defaultMessage: "Select scope" })}
                value={item.scopes?.join(', ') ?? ''}
                selectedOptions={item?.scopes}
                onOptionSelect={(e, data) => {
                  const selectedOptions = data.selectedOptions as string[];
                  setItem({ ...item, scopes: selectedOptions });
                }}
              >
                {scopeList.map((option) => (
                  <Option key={option}>
                    {option}
                  </Option>
                ))}
              </Dropdown>
            </Field>
            <Field label={intl.formatMessage({ defaultMessage: "Context" })}>
              <Dropdown
                multiselect
                placeholder={intl.formatMessage({ defaultMessage: "Select context" })}
                value={item.context?.join(', ') ?? ''}
                selectedOptions={item?.context}
                onOptionSelect={(e, data) => {
                  const selectedOptions = data.selectedOptions as string[];
                  setItem({ ...item, context: selectedOptions });
                }}
              >
                {contextList.map((option) => (
                  <Option key={option}>
                    {option}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          </DialogContent>
          <DialogActions className={classes.actions}>
            <Button appearance="secondary" onClick={() => { setOpen(false) }}>
              {intl.formatMessage({ defaultMessage: "Cancel" })}
            </Button>
            <Button disabled={hasError || hasUrlError || hasWebsiteUrlError} appearance="primary" onClick={() => { onAddOrEdit(item); setOpen(false) }}>
              {intl.formatMessage({ defaultMessage: "Confirm" })}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}